import React from 'react';
import { motion, LayoutGroup } from 'framer-motion';

/**
 * Two-option segmented toggle, styled to match the bottom `Navbar`
 * pill (white container, 8px padding, rounded-full, brown active
 * pill with cream label). When the user taps between options the
 * active pill slides via Framer Motion's `layoutId` animation —
 * same mechanic as the nav's active-tab indicator, so the two
 * components read as the same visual language.
 *
 * Pure presentation: the parent decides where to position it.
 */
const ACTIVE_BG = '#44231E';
const ACTIVE_FG = '#EFEBE0';
const INACTIVE_FG = '#8E7C69';

export default function SegmentedControl({
  value = 'note', // 'note' | 'image'
  onChange,
  className = '',
  labelsVariant = 'singular', // 'singular' | 'plural'
  options,
}) {
  const items = Array.isArray(options) && options.length === 2
    ? options.map((o) => ({ label: o.label, value: o.value }))
    : [
        { label: labelsVariant === 'plural' ? 'Memory notes' : 'Memory note', value: 'note' },
        { label: labelsVariant === 'plural' ? 'Pictures' : 'Picture', value: 'image' },
      ];

  return (
    <div className={className}>
      <LayoutGroup id="segmented-control">
        <div
          className="inline-flex items-center gap-1 rounded-full"
          style={{
            padding: 8,
            background: '#FFFFFF',
            boxShadow:
              '0 4px 10px rgba(64, 42, 20, 0.08), ' +
              '0 12px 28px rgba(64, 42, 20, 0.14)',
          }}
        >
          {items.map((item) => {
            const isActive = value === item.value;
            return (
              <motion.button
                key={item.value}
                onClick={() => onChange?.(item.value)}
                whileTap={{ scale: 0.94 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                className="relative focus:outline-none"
                style={{
                  height: 56,
                  borderRadius: 999,
                  border: 'none',
                  cursor: 'pointer',
                  background: 'transparent',
                  paddingLeft: 18,
                  paddingRight: 18,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isActive && (
                  <motion.span
                    layoutId="segmented-active-pill"
                    transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                    className="absolute inset-0 rounded-full"
                    style={{ background: ACTIVE_BG }}
                  />
                )}
                <span
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    color: isActive ? ACTIVE_FG : INACTIVE_FG,
                    fontFamily: 'Poppins',
                    fontSize: 15,
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </LayoutGroup>
    </div>
  );
}
